import { useEffect, useState } from 'react';
import { ACTIVATION_PLAN_OPTIONS, type ActivationOrderView, type DisplaySettingsView, type FreeActivationClaimView, type FreeActivationStatusView, type InviteTtlSeconds } from '@ai-voice/contracts';
import { ApiError, api } from '../api';
import { getLanguage, getLanguageTimeZone, t, tError } from '../i18n';
import { getDeviceId } from '../device';

const ACTIVE_ORDER_KEY = 'ai-voice-active-activation-order';
const FREE_CLAIM_KEY = 'ai-voice-free-activation-claim';

type Props = {
  displaySettings: DisplaySettingsView | null;
  onCodeReady: (code: string) => void;
};

export function ActivationPurchasePanel({ displaySettings, onCodeReady }: Props) {
  const [selectedTtl, setSelectedTtl] = useState<InviteTtlSeconds>(86_400);
  const [order, setOrder] = useState<ActivationOrderView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [clock, setClock] = useState(Date.now());
  const [freeStatus, setFreeStatus] = useState<FreeActivationStatusView | null>(null);
  const [freeClaim, setFreeClaim] = useState<FreeActivationClaimView | null>(() => readStoredFreeClaim());
  const [freeBusy, setFreeBusy] = useState(false);
  const [freeError, setFreeError] = useState('');
  const [freeNotice, setFreeNotice] = useState('');

  useEffect(() => {
    const timer = window.setInterval(() => setClock(Date.now()), 1000);
    const saved = localStorage.getItem(ACTIVE_ORDER_KEY);
    if (saved) {
      void api.activationOrder(saved, getDeviceId()).then(setOrder).catch(() => localStorage.removeItem(ACTIVE_ORDER_KEY));
    }
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let active = true;
    const refresh = async () => {
      try {
        const next = await api.freeActivationStatus(getDeviceId());
        if (active) setFreeStatus(next);
      } catch {
        if (active) setFreeStatus(null);
      }
    };
    void refresh();
    const timer = window.setInterval(() => void refresh(), 10_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, []);
  useEffect(() => {
    if (!order || order.status !== 'pending') return;
    let active = true;
    const refresh = async () => {
      try {
        const next = await api.activationOrder(order.orderId, getDeviceId());
        if (active) setOrder(next);
      } catch (cause) {
        if (active) setError(cause instanceof ApiError ? tError(cause.code, cause.message) : cause instanceof Error ? cause.message : String(cause));
      }
    };
    const timer = window.setInterval(() => void refresh(), 5000);
    void refresh();
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [order?.orderId, order?.status]);

  async function createOrder(ttlSeconds: InviteTtlSeconds): Promise<void> {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const next = await api.createActivationOrder(ttlSeconds, getDeviceId());
      localStorage.setItem(ACTIVE_ORDER_KEY, next.orderId);
      setOrder(next);
      setSelectedTtl(ttlSeconds);
    } catch (cause) {
      setError(cause instanceof ApiError ? tError(cause.code, cause.message) : cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  async function claimFreeActivation(): Promise<void> {
    setFreeBusy(true);
    setFreeError('');
    setFreeNotice('');
    try {
      const claimed = await api.claimFreeActivation(getDeviceId());
      localStorage.setItem(FREE_CLAIM_KEY, JSON.stringify(claimed));
      setFreeClaim(claimed);
      const nextStatus = await api.freeActivationStatus(getDeviceId()).catch(() => null);
      if (nextStatus) {
        setFreeStatus(nextStatus);
      } else {
        setFreeStatus((current) => current ? {
          ...current,
          state: 'claimed',
          canClaim: false,
          nextAvailableAt: nextBeijingMidnightIso()
        } : current);
      }
      setFreeNotice(t('免费激活码领取成功'));
    } catch (cause) {
      setFreeError(cause instanceof ApiError ? tError(cause.code, cause.message) : cause instanceof Error ? cause.message : String(cause));
    } finally {
      setFreeBusy(false);
    }
  }
  async function copy(value: string, message: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      setNotice(message);
      window.setTimeout(() => setNotice(''), 1800);
    } catch {
      setError(t("复制失败，请手动复制。"));
    }
  }

  function useCode(): void {
    if (!order?.activationCode) return;
    onCodeReady(order.activationCode);
    requestAnimationFrame(() => document.querySelector<HTMLInputElement>('.code-input')?.focus());
  }

  const remainingMs = order ? Math.max(0, new Date(order.expiresAt).getTime() - clock) : 0;
  const pending = order?.status === 'pending' && remainingMs > 0;
  const paid = order?.status === 'paid' && Boolean(order.activationCode);
  const freeRemainingMs = freeStatus?.nextAvailableAt ? Math.max(0, new Date(freeStatus.nextAvailableAt).getTime() - clock) : 0;
  const freeClaimUsable = Boolean(freeClaim && new Date(freeClaim.expiresAt).getTime() > clock);
  const freeCanClaim = Boolean(freeStatus?.canClaim && !freeBusy);
  const freeButtonLabel = freeClaimButtonText(freeStatus, freeRemainingMs, freeClaimUsable);
  const freeSectionVisible = freeClaimUsable || freeStatus?.state === 'active' || freeStatus?.state === 'claimed';
  const paidModeEnabled = displaySettings?.paidActivationEnabled !== false;
  const showPaidSection = paidModeEnabled || Boolean(order);

  function priceForTtl(ttlSeconds: InviteTtlSeconds): number {
    if (ttlSeconds === 86_400) return displaySettings?.activationPriceDay ?? 10;
    if (ttlSeconds === 604_800) return displaySettings?.activationPriceWeek ?? 50;
    if (ttlSeconds === 2_592_000) return displaySettings?.activationPriceMonth ?? 100;
    return 0;
  }

  return (
    <section className="activation-purchase">
      {freeSectionVisible && (
        <div className="free-activation-claim">
          <div>
            <span className="eyebrow">{t('限时福利')}</span>
            <h3>{t('免费领取激活码')}</h3>
            <p>{t('活动时间：{range}', { range: formatFreeActivationRange(freeStatus) })}</p>
          </div>
          {freeClaimUsable && freeClaim ? (
            <div className="free-claim-code">
              <span>{t('免费激活码已领取')}</span>
              <strong>{freeClaim.code}</strong>
              <small>{t('有效期至 {time}', { time: formatDateTime(freeClaim.expiresAt) })}</small>
              <div className="activation-paid-actions">
                <button className="secondary" onClick={() => void copy(freeClaim.code, t('激活码已复制'))} type="button">{t('复制激活码')}</button>
                <button className="primary" onClick={() => { onCodeReady(freeClaim.code); requestAnimationFrame(() => document.querySelector<HTMLInputElement>('.code-input')?.focus()); }} type="button">{t('填入并进入')}</button>
              </div>
            </div>
          ) : null}
          {freeStatus?.state === 'active' ? (
            <button className="primary free-claim-button" disabled={!freeCanClaim} onClick={() => void claimFreeActivation()} type="button">
              {freeBusy ? t('正在领取...') : freeButtonLabel}
            </button>
          ) : null}
          {freeStatus?.state === 'claimed' ? <div className="alert info">{freeButtonLabel}</div> : null}
          {freeNotice && <div className="alert success">{freeNotice}</div>}
          {freeError && <div className="alert error">{freeError}</div>}
        </div>
      )}
      {showPaidSection && (
        <>
          <div className="section-heading compact-heading">
            <div><span className="eyebrow">{t("自助购买")}</span><h2>{t("购买激活码")}</h2></div>
            <span className="muted">TRON · TRC20 · USDT</span>
          </div>
          {(!order || order.status === 'expired') && paidModeEnabled ? (
            <div className="activation-plan-grid">
              {ACTIVATION_PLAN_OPTIONS.map((plan) => (
                <button
                  className={`activation-plan ${selectedTtl === plan.ttlSeconds ? 'selected' : ''}`}
                  disabled={busy}
                  key={plan.ttlSeconds}
                  onClick={() => void createOrder(plan.ttlSeconds)}
                  type="button"
                >
                  <span>{t(plan.label)}</span>
                  <strong>{priceForTtl(plan.ttlSeconds)} <small>USDT</small></strong>
                </button>
              ))}
            </div>
          ) : null}

          {order && order.status !== 'paid' ? (
            <div className="activation-order">
              <div className="activation-order-head">
                <span>{t("订单 {id}", { id: order.orderId.slice(-8).toUpperCase() })}</span>
                <strong className={pending ? 'active' : 'expired'}>{pending ? t('剩余 {time}', { time: formatCountdown(remainingMs) }) : t('订单已过期')}</strong>
              </div>
              {pending ? (
                <>
                  <div className="activation-amount">
                    <span>{t("请精确支付")}</span>
                    <strong>{order.payableAmountUsdt} <small>USDT</small></strong>
                    <button className="mini" onClick={() => void copy(order.payableAmountUsdt, t("应付金额已复制"))}>{t("复制金额")}</button>
                  </div>
                  <div className="activation-address">
                    <span>{t("收款地址")}</span>
                    <code>{order.receivingAddress}</code>
                    <button className="mini" onClick={() => void copy(order.receivingAddress, t("收款地址已复制"))}>{t("复制地址")}</button>
                  </div>
                  <div className="alert warning">{t("只通过 TRON 网络支付 TRC20 USDT，不要使用 ERC20、BEP20 或其他网络。金额必须完全一致，否则无法自动发码。")}</div>
                </>
              ) : paidModeEnabled ? (
                <button className="primary" disabled={busy} onClick={() => void createOrder(order.ttlSeconds)} type="button">{t("重新生成订单")}</button>
              ) : null}
            </div>
          ) : null}

          {paid && order ? (
            <div className="activation-paid">
              <div>
                <span>{t("支付已确认")}</span>
                <strong>{order.activationCode}</strong>
                <small>{t("有效期至 {time}", { time: formatDateTime(order.activationExpiresAt ?? order.expiresAt) })}</small>
              </div>
              <div className="activation-paid-actions">
                <button className="secondary" onClick={() => void copy(order.activationCode!, t("激活码已复制"))} type="button">{t("复制激活码")}</button>
                <button className="primary" onClick={useCode} type="button">{t("填入并进入")}</button>
              </div>
            </div>
          ) : null}
        </>
      )}

      {notice && <div className="alert success">{notice}</div>}
      {error && <div className="alert error">{error}</div>}
    </section>
  );
}

function readStoredFreeClaim(): FreeActivationClaimView | null {
  try {
    const raw = localStorage.getItem(FREE_CLAIM_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as FreeActivationClaimView;
    if (!value.code || !value.expiresAt) return null;
    if (new Date(value.expiresAt).getTime() <= Date.now()) {
      localStorage.removeItem(FREE_CLAIM_KEY);
      return null;
    }
    return value;
  } catch {
    localStorage.removeItem(FREE_CLAIM_KEY);
    return null;
  }
}

function freeClaimButtonText(status: FreeActivationStatusView | null, remainingMs: number, hasCode: boolean): string {
  if (!status) return t('免费领取 3 天激活码');
  if (status.state === 'claimed') {
    return remainingMs > 0
      ? t('今日已领取，{time} 后可再次领取', { time: formatCountdown(remainingMs) })
      : t('今日已领取，明天可再次领取');
  }
  if (status.state === 'before') return t('活动尚未开始');
  if (status.state === 'ended' || status.state === 'disabled') return t('免费活动当前不可用');
  return hasCode ? t('再领取一张 3 天激活码') : t('免费领取 3 天激活码');
}

function nextBeijingMidnightIso(): string {
  const beijing = new Date(Date.now() + 8 * 60 * 60 * 1000);
  const nextUtc = Date.UTC(beijing.getUTCFullYear(), beijing.getUTCMonth(), beijing.getUTCDate() + 1);
  return new Date(nextUtc - 8 * 60 * 60 * 1000).toISOString();
}

function formatFreeActivationRange(status: FreeActivationStatusView | null): string {
  if (!status) return '--';
  const locale = getLanguage() === 'zh' ? 'zh-CN' : getLanguage() === 'ru' ? 'ru-RU' : 'en-US';
  const format = (value: string) => new Intl.DateTimeFormat(locale, {
    timeZone: getLanguageTimeZone(),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(new Date(value));
  const inclusiveEnd = new Date(new Date(status.endsAt).getTime() - 1);
  return `${format(status.startsAt)} – ${format(inclusiveEnd.toISOString())}`;
}
function formatCountdown(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat(getLanguage() === "zh" ? "zh-CN" : getLanguage() === "ru" ? "ru-RU" : "en-US", {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: getLanguageTimeZone()
  }).format(new Date(value));
}
